import React, { memo, useCallback, useEffect, useState } from 'react'
import { Box, Card, Divider, ListItemText, Stack } from '@mui/material'

import ExchangeProviderIcon from '../shared/exchangeProviderCard/ExchangeProviderIcon'
import { isExchangeProviderEmailType } from '../../util/app/app.helpers'
import SubmitButton from '../shared/button/SubmitButton'
import ConnectAccountField from './ConnectAccountField'
import CustomForm from '../shared/inputs/CustomForm'
import { toLower } from '../../util/string.helpers'
import { EXCHANGE_PROVIDER_PROCESS_STEP } from '../../constants/containerStates'
import AccountStatusLabel from './AccountStatusLabel'

const AccountCard = ({
  provider,
  label,
  membership = '',
  isForceBrandIcon,
  onSubmit,
  sx,
  t,
  providerId,
  error,
  accountSaveState,
  onChange,
  loading,
  ...restProps
}) => {
  const isConnected = Boolean(membership)
  const selectedProvider = providerId && provider.brandId === providerId

  const [value, setValue] = useState(membership)
  const [hasChanged, setHasChanged] = useState(false)

  useEffect(() => {
    setValue(membership)
  }, [membership])

  useEffect(() => {
    if (accountSaveState) {
      setHasChanged(false)
      if (
        accountSaveState ===
          EXCHANGE_PROVIDER_PROCESS_STEP.WRONG_ACCOUNT_NUMBER &&
        selectedProvider
      ) {
        setValue('')
      }
    }
  }, [accountSaveState, selectedProvider])

  const handleChange = useCallback(
    (newValue) => {
      setValue(newValue)
      const changed = toLower(newValue) !== toLower(membership)
      setHasChanged(changed)
      if (changed) onChange(newValue)
    },
    [membership, onChange]
  )

  const inputParams = provider.isOauthBrand
    ? {
        disabled: true
      }
    : {
        qrCodeEnabled: true,
        error: selectedProvider && value ? error : '',
        onChange: handleChange
      }

  return (
    <Card sx={[{ textAlign: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Stack spacing={2} alignItems='center' sx={{ p: 3 }}>
        <ExchangeProviderIcon
          provider={provider}
          isForceBrandIcon={isForceBrandIcon}
        />
        <ListItemText
          primary={label}
          slotProps={{ primary: { sx: { typography: 'subtitle1' } } }}
        />
        <AccountStatusLabel isConnected={isConnected} t={t} />
      </Stack>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <Box sx={{ p: 3 }}>
        <CustomForm onFormSubmit={() => onSubmit(provider, value)}>
          <ConnectAccountField
            provider={provider}
            value={value}
            {...inputParams}
            {...restProps}
            t={t}
          />
          <SubmitButton
            sx={{ mt: 3 }}
            size='default'
            label={getAccountLabel(provider.brandId, isConnected, t)}
            disabled={!hasChanged && !provider.isOauthBrand}
            loading={selectedProvider && loading}
          />
        </CustomForm>
      </Box>
    </Card>
  )
}

export default memo(AccountCard)

const getAccountLabel = (brandId, isConnected, t) =>
  t(
    `exchange.${isConnected ? 'change' : 'connect'}-${isExchangeProviderEmailType(brandId) ? 'email' : 'account'}`
  )
